import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Plus, Loader2, X, FileText, Handshake, Sparkles, Zap, ArrowRight, MessageCircle } from "lucide-react";
import ResponseStatusDropdown from "@/components/ResponseStatusDropdown";
import EmailComposeButtons from "@/components/EmailComposeButtons";

const STAGES = [
  { key: "pitched", label: "PITCHED" },
  { key: "negotiating", label: "NEGOTIATING" },
  { key: "signed", label: "SIGNED" },
  { key: "live", label: "LIVE" },
  { key: "paid", label: "PAID" },
];

const SAMPLE_EMAIL = `Hi there,

I'm Sarah from the Notion brand team. We're huge fans of your channel and would love to explore a sponsorship for Q1.

Deal we can offer:
- $4,500 USD flat fee
- 60-second integrated read in a video within the next 3 weeks
- Pinned top comment with our link (notion.so/creators)
- Deliverable due by March 15, 2026

Please include "#ad" in the first 3 seconds per FTC guidelines. We're happy to review the script before publish.

Let me know if this works and we'll send over the IO.

Best,
Sarah Chen
Brand Partnerships @ Notion`;

export default function Sponsors() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [sponsors, setSponsors] = useState([]);
  const [drag, setDrag] = useState(null);
  const [showParse, setShowParse] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [contractText, setContractText] = useState("");
  const [detail, setDetail] = useState(null);
  const [scoringId, setScoringId] = useState(null);
  const [batchScoring, setBatchScoring] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ brand: "", payout: "", stage: "pitched", deliverables: "", notes: "" });
  const [adding, setAdding] = useState(false);
  const [emailText, setEmailText] = useState("");
  const [emailParsing, setEmailParsing] = useState(false);
  const [showEmailPaste, setShowEmailPaste] = useState(false);
  const [responseStats, setResponseStats] = useState(null);

  useEffect(() => {
    api.get("/projects").then(({ data }) => { setProjects(data); if (data.length) setProjectId(data[0].project_id); });
  }, []);

  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/sponsors`).then(({ data }) => setSponsors(data));
  }, [projectId]);

  const refreshStats = () => {
    api.get("/sponsors/response-stats").then(({ data }) => setResponseStats(data)).catch(() => {});
  };
  useEffect(() => { refreshStats(); }, [projectId, sponsors.length]);

  const applyResponseUpdate = (updated) => {
    setSponsors((prev) => prev.map((s) => (s.sponsor_id === updated.sponsor_id ? updated : s)));
    if (detail?.sponsor_id === updated.sponsor_id) setDetail(updated);
    refreshStats();
  };

  const byStage = useMemo(() => {
    const map = Object.fromEntries(STAGES.map((s) => [s.key, []]));
    sponsors.forEach((s) => { (map[s.stage] ||= []).push(s); });
    return map;
  }, [sponsors]);

  const totalPipeline = useMemo(() => sponsors.reduce((n, s) => n + (s.payout || 0), 0), [sponsors]);

  const moveTo = async (stage) => {
    if (!drag || drag.stage === stage) return;
    const prev = sponsors;
    setSponsors((list) => list.map((s) => s.sponsor_id === drag.sponsor_id ? { ...s, stage } : s));
    try { await api.put(`/sponsors/${drag.sponsor_id}`, { stage }); toast.success(`Moved to ${stage}`); }
    catch { setSponsors(prev); toast.error("Could not move"); }
    setDrag(null);
  };

  const addManual = () => {
    if (!projectId) return toast.error("Create/select a project first");
    setAddForm({ brand: "", payout: "", stage: "pitched", deliverables: "", notes: "" });
    setEmailText("");
    setShowEmailPaste(false);
    setShowAdd(true);
  };

  const autoFillFromEmail = async () => {
    if (!emailText.trim()) return toast.error("Paste the email text first");
    setEmailParsing(true);
    try {
      const { data } = await api.post("/sponsors/parse-email", { text: emailText });
      setAddForm({
        brand: data.brand || "",
        payout: data.payout ? String(data.payout) : "",
        stage: data.stage || "pitched",
        deliverables: (data.deliverables || []).join("\n"),
        notes: data.notes || "",
      });
      setShowEmailPaste(false);
      toast.success("Auto-filled from email — review & save");
    } catch (err) {
      // 402 handled globally
      if (err?.response?.status !== 402) {
        toast.error(err?.response?.data?.detail || "Could not parse email");
      }
    } finally { setEmailParsing(false); }
  };

  const submitAdd = async (e) => {
    e?.preventDefault();
    const brand = addForm.brand.trim();
    if (!brand) return toast.error("Brand name is required");
    setAdding(true);
    const wasFirst = sponsors.length === 0;
    try {
      const payload = {
        brand,
        payout: Number(addForm.payout) || 0,
        stage: addForm.stage || "pitched",
        deliverables: addForm.deliverables.split("\n").map((s) => s.trim()).filter(Boolean),
        notes: addForm.notes.trim(),
      };
      const { data } = await api.post(`/projects/${projectId}/sponsors`, payload);
      setSponsors((s) => [data, ...s]);
      setShowAdd(false);
      if (wasFirst) {
        toast.success(`${data.brand} added — now score its fit against your channel`, {
          duration: 8000,
          action: {
            label: "Score fit →",
            onClick: () => setDetail(data),
          },
        });
      } else {
        toast.success(`${data.brand} added`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add sponsor");
    } finally { setAdding(false); }
  };

  const tryOnboarding = () => {
    if (!projectId) return toast.error("Create/select a project first");
    setAddForm({ brand: "", payout: "", stage: "pitched", deliverables: "", notes: "" });
    setEmailText(SAMPLE_EMAIL);
    setShowEmailPaste(true);
    setShowAdd(true);
  };

  const parseFromFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/contracts/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await createFromParsed(data);
    } catch (err) {
      toast.error("Could not parse contract");
    } finally {
      setParsing(false);
      e.target.value = "";
    }
  };

  const parseFromText = async () => {
    if (!contractText.trim()) return toast.error("Paste some text");
    setParsing(true);
    try {
      const blob = new Blob([contractText], { type: "text/plain" });
      const fd = new FormData(); fd.append("file", blob, "contract.txt");
      const { data } = await api.post("/contracts/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await createFromParsed(data);
      setContractText("");
    } catch { toast.error("Could not parse contract"); }
    finally { setParsing(false); }
  };

  const createFromParsed = async (p) => {
    const { data } = await api.post(`/projects/${projectId}/sponsors`, {
      brand: p.brand || "Untitled Brand",
      payout: p.payout || 0,
      deliverables: p.deliverables || [],
      notes: p.notes || "",
      stage: "signed",
    });
    // now update with the parsed extra fields
    const { data: updated } = await api.put(`/sponsors/${data.sponsor_id}`, {
      key_dates: p.key_dates || [],
      ftc_notes: p.ftc_notes || "",
      exclusivity: p.exclusivity || "",
    });
    setSponsors((s) => [updated, ...s]);
    setShowParse(false);
    toast.success(`Parsed ${updated.brand}`);
  };

  const deleteOne = async (id) => {
    const s = sponsors.find((x) => x.sponsor_id === id);
    await api.delete(`/sponsors/${id}`);
    setSponsors((s) => s.filter((x) => x.sponsor_id !== id));
    setDetail(null);
    toast.success(`${s?.brand || "Sponsor"} deleted`);
  };

  const scoreOne = async (sponsor_id) => {
    setScoringId(sponsor_id);
    try {
      const { data } = await api.post(`/sponsors/${sponsor_id}/fit-score`);
      setSponsors((list) => list.map((s) => s.sponsor_id === sponsor_id ? { ...s, fit_score: data } : s));
      setDetail((d) => d && d.sponsor_id === sponsor_id ? { ...d, fit_score: data } : d);
      toast.success(`Scored: ${data.score}/100 · ${data.verdict}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Score failed");
    } finally { setScoringId(null); }
  };

  const scoreAll = async () => {
    if (!projectId) return;
    setBatchScoring(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/sponsors/fit-score-batch`);
      toast.success(`Scored ${data.scored} sponsor${data.scored === 1 ? "" : "s"}`);
      // Refresh list
      const { data: fresh } = await api.get(`/projects/${projectId}/sponsors`);
      setSponsors(fresh);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch scoring failed");
    } finally { setBatchScoring(false); }
  };

  const scoreColor = (score) => score >= 70 ? "#34C759" : score >= 40 ? "#FFCC00" : "#FF3B30";

  return (
    <div className="min-h-screen">
      <div className="sticky top-0 z-30 bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-[#27272a]">
        <div className="px-4 md:px-8 min-h-16 flex flex-wrap items-center gap-2 md:gap-4 py-3 md:py-0">
          <Handshake size={16} strokeWidth={1.5} className="text-[#FF3B30]" />
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="sd-input h-9 max-w-[240px]"
            data-testid="sponsors-project-select"
          >
            {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.title}</option>)}
          </select>
          <div className="sd-label ml-2">TOTAL PIPELINE · <span className="font-mono text-white">${totalPipeline.toLocaleString()}</span></div>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={scoreAll}
              disabled={batchScoring || sponsors.length === 0}
              className="sd-btn sd-btn-ghost h-9"
              data-testid="sponsors-score-all-btn"
              title="AI-score all sponsors against your YouTube channel"
            >
              {batchScoring ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} strokeWidth={1.5} />}
              Score all
            </button>
            <button onClick={addManual} className="sd-btn sd-btn-ghost h-9" data-testid="sponsors-add-manual-btn">
              <Plus size={14} strokeWidth={1.5} /> Add sponsor
            </button>
            <button onClick={() => setShowParse(true)} className="sd-btn sd-btn-accent h-9" data-testid="sponsors-parse-btn">
              <Upload size={14} strokeWidth={1.5} /> Parse contract
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-8 overflow-x-auto">
        {/* Response Tracker KPI Strip */}
        {responseStats && responseStats.total_pitches > 0 && (
          <div className="mb-6 sd-card p-4 md:p-5" data-testid="response-tracker-strip">
            <div className="flex items-center gap-3 mb-3">
              <MessageCircle size={13} strokeWidth={1.5} className="text-[#FF3B30]" />
              <div className="sd-label">RESPONSE TRACKER · {responseStats.total_pitches} PITCHES</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <div className="border border-[#27272a] p-3">
                <div className="font-mono text-[10px] text-zinc-500 uppercase mb-1">Reply rate</div>
                <div className="font-display text-2xl text-[#34C759]" data-testid="stat-reply-rate">{responseStats.reply_rate_pct}%</div>
              </div>
              <div className="border border-[#27272a] p-3">
                <div className="font-mono text-[10px] text-zinc-500 uppercase mb-1">Positive</div>
                <div className="font-display text-2xl text-[#FFC107]" data-testid="stat-positive-rate">{responseStats.positive_rate_pct}%</div>
              </div>
              <div className="border border-[#27272a] p-3">
                <div className="font-mono text-[10px] text-zinc-500 uppercase mb-1">Awaiting</div>
                <div className="font-display text-2xl text-zinc-300">{responseStats.by_status.awaiting}</div>
              </div>
              <div className="border border-[#27272a] p-3">
                <div className="font-mono text-[10px] text-zinc-500 uppercase mb-1">Replied</div>
                <div className="font-display text-2xl text-[#34C759]">{responseStats.by_status.replied}</div>
              </div>
              <div className="border border-[#27272a] p-3">
                <div className="font-mono text-[10px] text-zinc-500 uppercase mb-1">Interested</div>
                <div className="font-display text-2xl text-[#FFC107]">{responseStats.by_status.interested}</div>
              </div>
              <div className="border border-[#27272a] p-3">
                <div className="font-mono text-[10px] text-zinc-500 uppercase mb-1">Ghosted</div>
                <div className="font-display text-2xl text-zinc-500">{responseStats.by_status.ghosted}</div>
              </div>
            </div>
            {responseStats.top_replying_brands?.length > 0 && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="sd-label text-zinc-500">TOP RESPONDERS ·</span>
                {responseStats.top_replying_brands.map((b) => (
                  <span key={b.brand} className="font-mono text-[10px] uppercase text-[#34C759] border border-[#34C759]/40 px-1.5 py-0.5">
                    {b.brand} · {b.replies}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
        {sponsors.length === 0 && (
          <div className="mb-6 sd-card p-6 md:p-10 sd-rise" data-testid="onboarding-hero">
            <div className="grid md:grid-cols-[1fr_auto] gap-6 md:gap-10 items-start">
              <div className="min-w-0">
                <div className="sd-label mb-3 text-[#FF3B30]">● SEE THE MAGIC IN 60 SECONDS</div>
                <h2 className="font-display text-3xl md:text-5xl leading-[1.05]">
                  Turn any sponsor email into a <span className="italic">tracked deal</span>.
                </h2>
                <p className="mt-4 text-zinc-400 max-w-xl leading-relaxed">
                  Paste an email → Claude Sonnet 4.5 extracts the brand, payout, deliverables, and FTC rules.
                  Then score how well the sponsor matches your audience — with a ready-to-send pitch angle.
                </p>

                <div className="mt-6 border border-[#27272a] bg-[#0a0a0a] p-4 max-w-2xl">
                  <div className="sd-label mb-2">SAMPLE EMAIL</div>
                  <pre className="text-xs text-zinc-300 leading-relaxed whitespace-pre-wrap font-sans line-clamp-6">
{SAMPLE_EMAIL.split("\n").slice(0, 6).join("\n") + "..."}
                  </pre>
                </div>

                <button
                  onClick={tryOnboarding}
                  className="sd-btn sd-btn-accent h-12 mt-6 px-6 text-[15px]"
                  data-testid="onboarding-try-btn"
                >
                  <Sparkles size={15} strokeWidth={1.5} />
                  Try it with this email
                  <ArrowRight size={14} strokeWidth={1.5} />
                </button>

                <div className="mt-4 flex flex-wrap items-center gap-3 text-xs font-mono text-zinc-500">
                  <span className="flex items-center gap-1.5"><span className="w-1 h-1 bg-[#FF3B30]"/> 1. Paste</span>
                  <span>→</span>
                  <span className="flex items-center gap-1.5"><span className="w-1 h-1 bg-[#FF3B30]"/> 2. Auto-fill</span>
                  <span>→</span>
                  <span className="flex items-center gap-1.5"><span className="w-1 h-1 bg-[#FF3B30]"/> 3. Score fit</span>
                  <span>→</span>
                  <span className="flex items-center gap-1.5"><span className="w-1 h-1 bg-[#34C759]"/> Ready to pitch</span>
                </div>
              </div>

              <div className="hidden md:block w-[280px] shrink-0">
                <div className="border border-[#27272a] bg-[#141414] p-5 sd-tracing">
                  <div className="sd-label">PREVIEW · KANBAN CARD</div>
                  <div className="mt-3 flex items-start justify-between gap-2">
                    <div className="font-display text-xl leading-tight">Notion</div>
                    <div className="font-mono text-sm text-[#34C759]">$4,500</div>
                  </div>
                  <ul className="mt-3 text-xs text-zinc-400 space-y-1">
                    <li>— 60s integrated read</li>
                    <li>— Pinned top comment</li>
                  </ul>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="font-mono text-[11px] text-zinc-500">Due: 2026-03-15</div>
                    <div className="font-mono text-[11px] px-2 py-0.5 border border-[#34C759]/40 text-[#34C759] flex items-center gap-1">
                      <Sparkles size={9} strokeWidth={1.5} /> 92
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-[10px] font-mono text-zinc-600 text-center">
                  What you'll see in ~15 seconds ↑
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-4 min-w-max">
          {STAGES.map((col) => (
            <div
              key={col.key}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => moveTo(col.key)}
              className="sd-kanban-col"
              data-testid={`kanban-col-${col.key}`}
            >
              <div className="px-4 py-3 border-b border-[#27272a] flex items-center justify-between">
                <div className="sd-label">{col.label}</div>
                <div className="font-mono text-xs text-zinc-500">{byStage[col.key]?.length || 0}</div>
              </div>
              <div className="p-3 space-y-3 min-h-[520px]">
                {byStage[col.key]?.map((s) => (
                  <div
                    key={s.sponsor_id}
                    draggable
                    onDragStart={() => setDrag(s)}
                    onClick={() => setDetail(s)}
                    className="border border-[#27272a] bg-[#141414] p-4 cursor-grab active:cursor-grabbing hover:border-zinc-500 transition-colors sd-rise"
                    data-testid={`sponsor-card-${s.sponsor_id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-display text-xl leading-tight">{s.brand}</div>
                      <div className="font-mono text-sm text-[#34C759]">${(s.payout || 0).toLocaleString()}</div>
                    </div>
                    {s.deliverables?.length > 0 && (
                      <ul className="mt-3 text-xs text-zinc-400 space-y-1">
                        {s.deliverables.slice(0, 2).map((d, i) => (
                          <li key={i} className="truncate">— {d}</li>
                        ))}
                      </ul>
                    )}
                    <div className="mt-3 flex items-center justify-between gap-2">
                      {s.key_dates?.[0] ? (
                        <div className="font-mono text-[11px] text-zinc-500 truncate">
                          {s.key_dates[0].label}: <span className="text-white">{s.key_dates[0].date}</span>
                        </div>
                      ) : <span />}
                      {s.fit_score?.score != null && (
                        <div
                          className="font-mono text-[11px] px-2 py-0.5 border flex items-center gap-1 shrink-0"
                          style={{ color: scoreColor(s.fit_score.score), borderColor: scoreColor(s.fit_score.score) + "66" }}
                          data-testid={`fit-badge-${s.sponsor_id}`}
                        >
                          <Sparkles size={9} strokeWidth={1.5} /> {s.fit_score.score}
                        </div>
                      )}
                    </div>
                    {/* Response Status Pill — only for outreach stages */}
                    {["pitched", "negotiating"].includes(s.stage) && (
                      <div className="mt-2 flex justify-end">
                        <ResponseStatusDropdown
                          sponsor={s}
                          variant="pill"
                          onChange={applyResponseUpdate}
                        />
                      </div>
                    )}
                  </div>
                ))}
                {byStage[col.key]?.length === 0 && (
                  <div className="text-xs text-zinc-600 px-2 py-4">Empty. Drag a card here.</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Sponsor modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => !adding && setShowAdd(false)}>
          <form onSubmit={submitAdd} className="bg-[#0e0e0f] border border-[#27272a] w-full max-w-md p-8" onClick={(e) => e.stopPropagation()} data-testid="add-sponsor-modal">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="sd-label mb-2">NEW SPONSOR</div>
                <h3 className="font-display text-3xl">Add to pipeline</h3>
              </div>
              <button type="button" onClick={() => !adding && setShowAdd(false)} className="text-zinc-500 hover:text-white">
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>

            {/* AI Auto-fill from email */}
            <div className="mb-6 border border-[#27272a] bg-[#0a0a0a]">
              <button
                type="button"
                onClick={() => setShowEmailPaste(!showEmailPaste)}
                className="w-full px-4 py-3 flex items-center gap-2 text-left hover:bg-[#141414] transition-colors"
                data-testid="autofill-toggle-btn"
              >
                <Sparkles size={14} strokeWidth={1.5} className="text-[#FF3B30]" />
                <div className="flex-1">
                  <div className="sd-label text-[#FF3B30]">AI AUTO-FILL · CLAUDE SONNET 4.5</div>
                  <div className="text-xs text-zinc-400 mt-0.5">Paste a sponsor email, let AI fill this form.</div>
                </div>
                <span className="text-zinc-500 text-xs">{showEmailPaste ? "−" : "+"}</span>
              </button>
              {showEmailPaste && (
                <div className="px-4 pb-4 sd-tracing">
                  <textarea
                    value={emailText}
                    onChange={(e) => setEmailText(e.target.value)}
                    placeholder="Paste the sponsor's negotiation email here…"
                    className="sd-textarea min-h-[100px]"
                    disabled={emailParsing}
                    data-testid="autofill-email-textarea"
                  />
                  <button
                    type="button"
                    onClick={autoFillFromEmail}
                    disabled={emailParsing || !emailText.trim()}
                    className="sd-btn sd-btn-accent h-9 mt-3 w-full"
                    data-testid="autofill-submit-btn"
                  >
                    {emailParsing ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} strokeWidth={1.5} />}
                    Auto-fill form
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="sd-label block mb-2">BRAND *</label>
                <input
                  autoFocus
                  value={addForm.brand}
                  onChange={(e) => setAddForm((f) => ({ ...f, brand: e.target.value }))}
                  placeholder="e.g. Notion"
                  className="sd-input"
                  disabled={adding}
                  data-testid="add-sponsor-brand-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="sd-label block mb-2">PAYOUT ($)</label>
                  <input
                    type="number"
                    value={addForm.payout}
                    onChange={(e) => setAddForm((f) => ({ ...f, payout: e.target.value }))}
                    placeholder="4500"
                    className="sd-input"
                    disabled={adding}
                    data-testid="add-sponsor-payout-input"
                  />
                </div>
                <div>
                  <label className="sd-label block mb-2">STAGE</label>
                  <select
                    value={addForm.stage}
                    onChange={(e) => setAddForm((f) => ({ ...f, stage: e.target.value }))}
                    className="sd-input"
                    disabled={adding}
                    data-testid="add-sponsor-stage-select"
                  >
                    {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="sd-label block mb-2">DELIVERABLES (ONE PER LINE)</label>
                <textarea
                  value={addForm.deliverables}
                  onChange={(e) => setAddForm((f) => ({ ...f, deliverables: e.target.value }))}
                  placeholder={"60s integrated read\npinned comment"}
                  className="sd-textarea min-h-[80px]"
                  disabled={adding}
                  data-testid="add-sponsor-deliverables-textarea"
                />
              </div>

              <div>
                <label className="sd-label block mb-2">NOTES</label>
                <textarea
                  value={addForm.notes}
                  onChange={(e) => setAddForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Context, contact, next steps…"
                  className="sd-textarea min-h-[60px]"
                  disabled={adding}
                  data-testid="add-sponsor-notes-textarea"
                />
              </div>
            </div>

            <div className="mt-6 flex items-center gap-3">
              <button type="button" onClick={() => !adding && setShowAdd(false)} className="sd-btn sd-btn-ghost h-10 flex-1" disabled={adding}>
                Cancel
              </button>
              <button type="submit" disabled={adding || !addForm.brand.trim()} className="sd-btn sd-btn-accent h-10 flex-1" data-testid="add-sponsor-submit-btn">
                {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} strokeWidth={1.5} />}
                Add sponsor
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Parse modal */}
      {showParse && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => !parsing && setShowParse(false)}>
          <div className="bg-[#0e0e0f] border border-[#27272a] w-full max-w-2xl p-8" onClick={(e) => e.stopPropagation()} data-testid="parse-modal">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="sd-label mb-2">CONTRACT INTAKE · CLAUDE SONNET 4.5</div>
                <h3 className="font-display text-3xl">Parse a sponsorship contract</h3>
              </div>
              <button onClick={() => !parsing && setShowParse(false)} className="text-zinc-500 hover:text-white">
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <label className="sd-card p-6 cursor-pointer hover:border-zinc-500 flex flex-col items-start gap-3">
                <FileText size={20} strokeWidth={1.25} className="text-[#FF3B30]" />
                <div className="font-display text-xl">Upload PDF</div>
                <div className="text-xs text-zinc-500">We'll extract brand, payout, deliverables, dates, FTC rules.</div>
                <input type="file" accept="application/pdf,.pdf,.txt" onChange={parseFromFile} className="hidden" disabled={parsing} data-testid="parse-file-input" />
                <span className="sd-btn sd-btn-primary h-9 mt-2">
                  {parsing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} strokeWidth={1.5} />}
                  Choose file
                </span>
              </label>
              <div className="sd-card p-6">
                <div className="sd-label mb-2">OR PASTE TEXT</div>
                <textarea
                  value={contractText}
                  onChange={(e) => setContractText(e.target.value)}
                  placeholder="Paste contract text here…"
                  className="sd-textarea min-h-[140px]"
                  disabled={parsing}
                  data-testid="parse-text-textarea"
                />
                <button onClick={parseFromText} disabled={parsing} className="sd-btn sd-btn-accent h-9 mt-3 w-full" data-testid="parse-text-btn">
                  {parsing ? <Loader2 size={14} className="animate-spin" /> : "Parse text"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail drawer */}
      {detail && (
        <div className="fixed inset-0 z-40 bg-black/60" onClick={() => setDetail(null)}>
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-[#0e0e0f] border-l border-[#27272a] p-8 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <div className="sd-label mb-2">SPONSOR</div>
                <h3 className="font-display text-3xl">{detail.brand}</h3>
              </div>
              <button onClick={() => setDetail(null)} className="text-zinc-500 hover:text-white"><X size={18} strokeWidth={1.5} /></button>
            </div>
            <div className="mt-6 space-y-4">
              {/* Response Tracker in detail drawer */}
              <div>
                <div className="sd-label mb-2 flex items-center gap-2"><MessageCircle size={11} strokeWidth={1.5} /> BRAND RESPONSE</div>
                <ResponseStatusDropdown
                  sponsor={detail}
                  variant="full"
                  onChange={applyResponseUpdate}
                />
                {detail.response_updated_at && (
                  <div className="mt-1 font-mono text-[10px] text-zinc-500">
                    Updated {new Date(detail.response_updated_at).toLocaleString()}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-[#27272a] p-3">
                  <div className="sd-label">PAYOUT</div>
                  <div className="font-mono text-xl mt-1">${(detail.payout || 0).toLocaleString()}</div>
                </div>
                <div className="border border-[#27272a] p-3">
                  <div className="sd-label">STAGE</div>
                  <div className="font-mono text-lg mt-1">{detail.stage}</div>
                </div>
              </div>
              {detail.deliverables?.length > 0 && (
                <div>
                  <div className="sd-label mb-2">DELIVERABLES</div>
                  <ul className="text-sm text-zinc-300 space-y-1">
                    {detail.deliverables.map((d, i) => <li key={i}>— {d}</li>)}
                  </ul>
                </div>
              )}
              {detail.key_dates?.length > 0 && (
                <div>
                  <div className="sd-label mb-2">KEY DATES</div>
                  <ul className="text-xs font-mono text-zinc-300 space-y-1">
                    {detail.key_dates.map((d, i) => <li key={i}>{d.label}: <span className="text-white">{d.date}</span></li>)}
                  </ul>
                </div>
              )}
              {detail.ftc_notes && (
                <div>
                  <div className="sd-label mb-2">FTC NOTES</div>
                  <div className="text-sm text-zinc-300 leading-relaxed">{detail.ftc_notes}</div>
                </div>
              )}
              {detail.exclusivity && (
                <div>
                  <div className="sd-label mb-2">EXCLUSIVITY</div>
                  <div className="text-sm text-zinc-300 leading-relaxed">{detail.exclusivity}</div>
                </div>
              )}
              {detail.notes && (
                <div>
                  <div className="sd-label mb-2">NOTES</div>
                  <div className="text-sm text-zinc-300 leading-relaxed">{detail.notes}</div>
                </div>
              )}

              {/* AI Sponsor Fit Score */}
              <div className="border-t border-[#27272a] pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="sd-label">SPONSOR FIT · CLAUDE SONNET 4.5</div>
                  <button
                    onClick={() => scoreOne(detail.sponsor_id)}
                    disabled={scoringId === detail.sponsor_id}
                    className="sd-btn sd-btn-ghost h-8 text-xs"
                    data-testid="fit-score-btn"
                  >
                    {scoringId === detail.sponsor_id ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} strokeWidth={1.5} />}
                    {detail.fit_score ? "Re-score" : "Score fit"}
                  </button>
                </div>

                {!detail.fit_score && scoringId !== detail.sponsor_id && (
                  <div className="text-xs text-zinc-500 leading-relaxed">
                    Get an AI score (0–100) for how well this sponsor matches your channel's audience, plus a
                    ready-to-send pitch angle. Requires a connected YouTube channel.
                  </div>
                )}

                {scoringId === detail.sponsor_id && (
                  <div className="sd-tracing pl-3 border-l border-[#27272a] py-4 text-sm text-zinc-400">
                    Analyzing your recent uploads against {detail.brand}…
                  </div>
                )}

                {detail.fit_score && scoringId !== detail.sponsor_id && (
                  <div className="space-y-4">
                    <div className="flex items-baseline gap-4">
                      <div
                        className="font-mono text-6xl"
                        style={{ color: scoreColor(detail.fit_score.score) }}
                        data-testid="fit-score-value"
                      >
                        {detail.fit_score.score}
                      </div>
                      <div>
                        <div className="sd-label">VERDICT</div>
                        <div className="font-display text-xl mt-1" style={{ color: scoreColor(detail.fit_score.score) }}>
                          {detail.fit_score.verdict}
                        </div>
                        <div className="text-xs text-zinc-500 mt-1">confidence: {detail.fit_score.confidence}</div>
                      </div>
                    </div>

                    <div>
                      <div className="sd-label mb-1">ALIGNMENT</div>
                      <div className="text-sm text-zinc-300 leading-relaxed">{detail.fit_score.alignment}</div>
                    </div>

                    <div>
                      <div className="sd-label mb-1">PITCH ANGLE</div>
                      <div className="border border-[#27272a] p-3 text-sm text-zinc-200 leading-relaxed">
                        {detail.fit_score.pitch_angle}
                        <button
                          onClick={() => { navigator.clipboard.writeText(detail.fit_score.pitch_angle); toast.success("Copied"); }}
                          className="mt-2 text-xs text-zinc-500 hover:text-white transition-colors block"
                          data-testid="fit-pitch-copy-btn"
                        >
                          Copy pitch →
                        </button>
                      </div>
                    </div>

                    {detail.fit_score.integration_format && (
                      <div>
                        <div className="sd-label mb-1">SUGGESTED FORMAT</div>
                        <div className="font-mono text-sm text-white">{detail.fit_score.integration_format}</div>
                      </div>
                    )}

                    {detail.fit_score.risks?.length > 0 && (
                      <div>
                        <div className="sd-label mb-1">RISKS</div>
                        <ul className="text-sm text-zinc-400 space-y-1">
                          {detail.fit_score.risks.map((r, i) => <li key={i}>— {r}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Draft pitch email (from marketplace apply-with-ai) */}
              {detail.draft_pitch_email && (
                <div className="border-t border-[#27272a] pt-6 mt-6" data-testid="sponsor-draft-pitch-section">
                  <div className="sd-label mb-4 flex items-center gap-2">
                    <span className="text-[#FF3B30]">●</span> DRAFTED PITCH EMAIL
                  </div>
                  <div className="space-y-3">
                    <div className="border border-[#27272a] p-3">
                      <div className="sd-label mb-1 text-zinc-500">SUBJECT</div>
                      <div className="text-sm font-mono">{detail.draft_pitch_email.subject}</div>
                    </div>
                    <div className="border border-[#27272a] p-3">
                      <div className="sd-label mb-1 flex items-center justify-between text-zinc-500">
                        BODY
                        <button
                          onClick={() => { navigator.clipboard.writeText(`Subject: ${detail.draft_pitch_email.subject}\n\n${detail.draft_pitch_email.body}`); toast.success("Full email copied"); }}
                          className="text-zinc-500 hover:text-white"
                          data-testid="sponsor-copy-pitch-btn"
                        >
                          Copy full →
                        </button>
                      </div>
                      <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-sans leading-relaxed max-h-64 overflow-y-auto">{detail.draft_pitch_email.body}</pre>
                    </div>
                    {detail.draft_pitch_email.cta && (
                      <div className="text-xs italic text-zinc-400">CTA: "{detail.draft_pitch_email.cta}"</div>
                    )}
                    {/* One-click open in mail client */}
                    <div className="pt-2">
                      <div className="sd-label mb-2 text-zinc-500">SEND VIA</div>
                      <EmailComposeButtons
                        email={detail.draft_pitch_email}
                        testIdPrefix="sponsor-compose"
                      />
                    </div>
                  </div>
                </div>
              )}

              <button onClick={() => deleteOne(detail.sponsor_id)} className="sd-btn sd-btn-ghost h-9 w-full mt-4 hover:!text-[#FF3B30]" data-testid="sponsor-delete-btn">
                Delete sponsor
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
